import http from '@/api'
import { checkAuthentication, logout } from '@/auth/authentication'
import store from '@/store'

import { ROUTE_NAMES } from '../router'

/**
 * Handles redirect query parameters and performs redirection.
 * Uses Vue Router 4 return-based API (no next() callback).
 */
export function redirectGuard(to, from) {
  // Skip redirect processing for login route
  if (to.name === ROUTE_NAMES.LOGIN) {
    return
  }

  // Check if we have a redirect query parameter
  if (to.query.redirect) {
    // Create a clean version of the query params without the redirect
    const query = { ...to.query }
    const redirectPath = query.redirect
    delete query.redirect

    // Prevent infinite redirects
    if (redirectPath === to.fullPath || redirectPath === from.fullPath) {
      console.warn('Prevented redirect loop')
      return { ...to, query, replace: true }
    }

    // Perform the redirect with clean query params
    return {
      path: redirectPath,
      replace: true,
      query,
    }
  }

  // No redirect param, continue navigation (return nothing)
}

/**
 * Handles authentication checks and protects routes.
 * Uses Vue Router 4 return-based API (no next() callback).
 */
export async function authGuard(to) {
  const noAuth = to.meta?.requireAuth === false
  const noRedirect = to.meta?.noRedirect === true
  try {
    // Always allow access to routes that don't require auth
    if (noAuth) return
    if (noRedirect) return
    // Try to authenticate the user
    await checkAuthentication()
    // Normal navigation for authenticated users (return nothing)
  } catch {
    // Authentication failed — redirect to login with original path
    return {
      name: ROUTE_NAMES.LOGIN,
      query: { redirect: to.fullPath },
      replace: true,
    }
  }
}

/**
 * Cancels pending API requests when changing routes.
 * Uses Vue Router 4 return-based API (no next() callback).
 */
export function cancelPendingRequests(to, from) {
  // Only cancel on actual page navigations (path change), not query-only
  // changes like tab switches within the same page.
  if (from.name !== null && from.path !== '/' && to.path !== from.path) {
    http.requests.cancelAll('Cancelled requests due to navigation')
  }
}

function discardNotifications() {
  setTimeout(() => {
    store.commit('notify', null)
  }, 6000)
}

export async function onLogout() {
  const resetStore = true
  const clearBackendCache = true
  await logout(resetStore, clearBackendCache)
  discardNotifications()
  return { name: 'landing' }
}

export function getCloneQParams(
  q = { clone: '', attributes: true, annotations: false, permissions: false },
) {
  const params = {}
  const notAttributes = q.attributes === false || q.attributes === 'false'
  const annotations = q.annotations === true || q.annotations === 'true'
  const permissions = q.permissions === true || q.permissions === 'true'
  if (q.clone) {
    params.cloneData = { id: q.clone }
    params.cloneData.keepAttributes = !notAttributes
    params.cloneData.keepAnnotations = annotations
    params.cloneData.keepPermissions = permissions
  }
  return params
}
